import { parseDate } from "@internationalized/date";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import clsx from "clsx";
import type {
  EditorState,
  LexicalEditor,
  SerializedEditorState,
} from "lexical";
import { CheckIcon, ChevronDown, CopyIcon, MailIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button as AriaButton,
  composeRenderProps,
  Heading,
  Label,
  MenuTrigger,
} from "react-aria-components";
import { z } from "zod";

import { roleHasPermission } from "@/auth/shared";
import { Alert } from "@/components/base/alert";
import { Button } from "@/components/base/button";
import type { DataTableColumn } from "@/components/base/data-table";
import { DataTable } from "@/components/base/data-table";
import { DatePicker } from "@/components/base/date-picker";
import {
  itemStyles,
  popoverStyles,
} from "@/components/base/form/fields/multi-select";
import { inputStyles } from "@/components/base/form/fields/shared";
import { Modal } from "@/components/base/modal";
import { Pagination } from "@/components/base/pagination";
import { Popover } from "@/components/base/popover";
import { title } from "@/components/base/primitives";
import { RichTextEditor } from "@/components/base/rich-text-editor/editor";
import { Select } from "@/components/base/select";
import { TextField } from "@/components/base/text-field";
import { addToast } from "@/components/base/toast";
import { Toolbar } from "@/components/base/toolbar";
import { focusRing } from "@/components/base/utils";
import type { LexicalState } from "@/db/schema/shared";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";

const searchSchema = z.object({
  venueIds: z.array(z.number()).optional().default([]),
  gender: z.enum(["male", "female", "coed"]).optional(),
  levelIds: z.array(z.number()).optional().default([]),
  divisionIds: z.array(z.number()).optional().default([]),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  page: z.number().optional().default(1),
  pageSize: z.number().optional().default(50),
});

export const Route = createFileRoute("/td/players")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context: { queryClient, trpc, ...context } }) => {
    const viewer = context.viewer;

    const hasPermission =
      viewer &&
      roleHasPermission(viewer.role, {
        tournaments: ["update"],
      });

    if (!hasPermission) {
      throw redirect({ to: "/not-found" });
    }

    queryClient.prefetchQuery(trpc.directors.getViewerVenues.queryOptions());
    queryClient.prefetchQuery(trpc.directors.getLevels.queryOptions());
    queryClient.prefetchQuery(trpc.directors.getDivisions.queryOptions());
  },
  component: RouteComponent,
});

type PlayerRow = {
  id: number;
  firstName: string;
  preferredName: string | null;
  lastName: string;
  gender: string;
  levelName: string | null;
  email: string | null;
  phoneNumber: string | null;
  lastTournamentDate: string | null;
};

// ---------------------------------------------------------------------------
// Multi-select filter (reuses tournament filter pattern)
// ---------------------------------------------------------------------------

function MultiSelectFilter<V extends string | number>({
  label,
  placeholder,
  options,
  values,
  searchKey,
}: {
  label: string;
  placeholder: string;
  options: { value: V; display: string }[];
  values: Set<V>;
  searchKey: string;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <MenuTrigger>
        <AriaButton
          className={(render) =>
            clsx(
              "flex items-center justify-between text-start gap-4 w-full cursor-default px-2 py-1.5 bg-white hover:bg-gray-200",
              focusRing(render),
              inputStyles(render),
            )
          }
        >
          <div className="flex-1 truncate text-sm">
            {values.size === 0 ? (
              <span className="text-placeholder italic">{placeholder}</span>
            ) : (
              options
                .filter(({ value }) => values.has(value))
                .map(({ display }) => display)
                .join(", ")
            )}
          </div>
          <ChevronDown
            aria-hidden
            className="mx-2 h-4 w-4 shrink-0 text-gray-600"
          />
        </AriaButton>
        <Popover
          className={composeRenderProps(
            "min-w-(--trigger-width) overflow-y-scroll",
            (className, render) => popoverStyles({ ...render, className }),
          )}
        >
          {options.map(({ value, display }) => (
            <Link
              key={value}
              to="/td/players"
              search={(search) => {
                const current = (search[searchKey] as V[]) ?? [];
                return {
                  ...search,
                  page: 1,
                  [searchKey]: values.has(value)
                    ? current.filter((v) => v !== value)
                    : current.concat(value),
                };
              }}
              className={itemStyles({
                className:
                  "w-full hover:bg-gray-200 flex flex-row justify-between",
              })}
            >
              <span className="group-selected:font-semibold flex flex-1 items-center gap-2 truncate font-normal">
                {display}
              </span>
              <span className="flex w-5 items-center">
                {values.has(value) && <CheckIcon className="h-4 w-4" />}
              </span>
            </Link>
          ))}
        </Popover>
      </MenuTrigger>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Route component
// ---------------------------------------------------------------------------

function RouteComponent() {
  const trpc = useTRPC();
  const search = Route.useSearch();
  const navigate = useNavigate();

  const { data: venues } = useSuspenseQuery(
    trpc.directors.getViewerVenues.queryOptions(),
  );

  const { data: allLevels } = useSuspenseQuery(
    trpc.directors.getLevels.queryOptions(),
  );

  const { data: allDivisions } = useSuspenseQuery(
    trpc.directors.getDivisions.queryOptions(),
  );

  const singleVenue = venues.length === 1 ? venues[0] : null;

  const levelOptions = useMemo(
    () =>
      allLevels.map((l) => ({
        value: l.id,
        display: l.name.toUpperCase(),
      })),
    [allLevels],
  );

  const divisionOptions = useMemo(
    () =>
      allDivisions.map((d) => ({
        value: d.id,
        display: d.display ?? d.name.toUpperCase(),
      })),
    [allDivisions],
  );

  const venueOptions = useMemo(
    () =>
      venues.map((v) => ({
        value: v.id,
        display: `${v.name}, ${v.city}`,
      })),
    [venues],
  );

  // When a single venue, always use it; otherwise use the search param selection
  const effectiveVenueIds = singleVenue ? [singleVenue.id] : search.venueIds;

  const genderOptions = [
    { value: "male" as const, display: "Men's" },
    { value: "female" as const, display: "Women's" },
    { value: "coed" as const, display: "Coed" },
  ];

  const hasVenues = effectiveVenueIds.length > 0;

  const { data: results, isLoading } = useQuery({
    ...trpc.directors.searchPlayers.queryOptions({
      venueIds: effectiveVenueIds,
      gender: search.gender,
      levelIds: search.levelIds.length > 0 ? search.levelIds : undefined,
      divisionIds:
        search.divisionIds.length > 0 ? search.divisionIds : undefined,
      dateFrom: search.dateFrom,
      dateTo: search.dateTo,
      page: search.page,
      pageSize: search.pageSize,
    }),
    enabled: hasVenues,
  });

  const players = (results?.data ?? []) as PlayerRow[];
  const totalPages = results?.totalPages ?? 0;
  const totalItems = results?.totalItems ?? 0;

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Clear selection when search params change (page, filters, etc.)
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally reacting to search param changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [search]);

  const [emailModalOpen, setEmailModalOpen] = useState(false);

  function updateSearch(updates: Record<string, unknown>) {
    navigate({
      to: "/td/players",
      search: (prev) => ({
        ...prev,
        ...updates,
        page: 1,
      }),
    });
  }

  const columns: DataTableColumn<PlayerRow>[] = [
    {
      id: "name",
      header: "Name",
      isRowHeader: true,
      minWidth: 120,
      cell: (player) => (
        <span>
          {player.preferredName || player.firstName} {player.lastName}
        </span>
      ),
    },
    {
      id: "gender",
      header: "Gender",
      width: 80,
      minWidth: 80,
      cell: (player) => <span className="capitalize">{player.gender}</span>,
    },
    {
      id: "level",
      header: "Rating",
      width: 80,
      minWidth: 80,
      cell: (player) => (
        <span className="uppercase">{player.levelName ?? "—"}</span>
      ),
    },
    {
      id: "email",
      header: "Email",
      minWidth: 160,
      cell: (player) => (
        <span className="block truncate">{player.email ?? "—"}</span>
      ),
    },
    {
      id: "phone",
      header: "Phone",
      width: 130,
      minWidth: 130,
      cell: (player) => <span>{player.phoneNumber ?? "—"}</span>,
    },
    {
      id: "lastTournament",
      header: "Last Tournament",
      width: 130,
      minWidth: 130,
      cell: (player) => <span>{player.lastTournamentDate ?? "—"}</span>,
    },
  ];

  return (
    <DefaultLayout
      classNames={{
        content:
          "flex flex-col space-y-6 w-full max-w-full md:max-w-5xl px-3 py-12 mx-auto",
      }}
    >
      <h2 className={title({ size: "sm" })}>Player Search</h2>

      {/* Filter bar */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-3">
          {singleVenue ? (
            <span className="self-center text-sm text-gray-600">
              {singleVenue.name}, {singleVenue.city}
            </span>
          ) : (
            <div className="min-w-[200px] flex-1">
              <MultiSelectFilter
                label="Venues"
                placeholder="Select venues..."
                options={venueOptions}
                values={new Set(search.venueIds)}
                searchKey="venueIds"
              />
            </div>
          )}

          <div className="w-[130px]">
            <Select
              label="Gender"
              className="bg-white"
              options={genderOptions}
              selectedKey={search.gender ?? null}
              onSelectionChange={(key) => {
                updateSearch({ gender: key || undefined });
              }}
              placeholder="All"
            />
          </div>

          <div className="w-[140px]">
            <MultiSelectFilter
              label="Rating"
              placeholder="All"
              options={levelOptions}
              values={new Set(search.levelIds)}
              searchKey="levelIds"
            />
          </div>

          <div className="w-[180px]">
            <MultiSelectFilter
              label="Played in Division"
              placeholder="All"
              options={divisionOptions}
              values={new Set(search.divisionIds)}
              searchKey="divisionIds"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="w-[170px]">
            <DatePicker
              label="From Date"
              value={search.dateFrom ? parseDate(search.dateFrom) : null}
              onChange={(date) =>
                updateSearch({ dateFrom: date?.toString() ?? undefined })
              }
            />
          </div>
          <div className="w-[170px]">
            <DatePicker
              label="To Date"
              value={search.dateTo ? parseDate(search.dateTo) : null}
              onChange={(date) =>
                updateSearch({ dateTo: date?.toString() ?? undefined })
              }
            />
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <Toolbar>
        <Button
          variant="outline"
          isDisabled={selectedIds.size === 0}
          onPress={() => setEmailModalOpen(true)}
        >
          <MailIcon size={14} />
          Email {selectedIds.size} Selected
        </Button>
        <Button
          variant="outline"
          isDisabled={selectedIds.size === 0}
          onPress={async () => {
            const emails = players
              .filter((p) => selectedIds.has(p.id) && p.email)
              .map((p) => p.email);
            if (emails.length === 0) {
              addToast({ variant: "info", title: "No emails found" });
              return;
            }
            try {
              await navigator.clipboard.writeText(emails.join(", "));
              addToast({
                variant: "success",
                title: "Copied!",
                description: `${emails.length} email${emails.length === 1 ? "" : "s"} copied`,
              });
            } catch {
              addToast({ variant: "error", title: "Failed to copy emails" });
            }
          }}
        >
          <CopyIcon size={14} />
          Copy Emails
        </Button>
        {hasVenues && (
          <span className="self-center text-sm text-gray-500">
            {totalItems} {totalItems === 1 ? "player" : "players"} found
          </span>
        )}
      </Toolbar>

      {/* Results table */}
      {!hasVenues ? (
        <p className="text-sm text-gray-500 italic">
          Select a venue to search players.
        </p>
      ) : (
        <div className="flex max-w-full flex-col items-center gap-4 overflow-hidden">
          <DataTable
            aria-label="Player search results"
            data={players}
            columns={columns}
            getRowId={(p) => p.id}
            selectionMode="multiple"
            selectedKeys={selectedIds}
            onSelectionChange={(selection) => {
              if (selection === "all") {
                setSelectedIds(new Set(players.map((p) => p.id)));
              } else {
                setSelectedIds(
                  new Set(
                    [...selection].filter(
                      (k): k is number => typeof k === "number",
                    ),
                  ),
                );
              }
            }}
            renderEmptyState={() => (
              <div className="p-4 text-center text-gray-500 italic">
                {isLoading ? "Searching..." : "No players found."}
              </div>
            )}
          />

          {totalPages > 1 && (
            <Pagination
              to="/td/players"
              page={search.page}
              pageSize={search.pageSize}
              pageInfo={{
                totalItems,
                totalPages,
              }}
            />
          )}
        </div>
      )}

      {/* Email modal */}
      {emailModalOpen && (
        <EmailPlayersModal
          isOpen={emailModalOpen}
          onOpenChange={setEmailModalOpen}
          playerProfileIds={[...selectedIds]}
        />
      )}
    </DefaultLayout>
  );
}

function EmailPlayersModal({
  isOpen,
  onOpenChange,
  playerProfileIds,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  playerProfileIds: number[];
}) {
  const trpc = useTRPC();
  const [subject, setSubject] = useState("");
  const [editorState, setEditorState] = useState<SerializedEditorState | null>(
    null,
  );
  const [successCount, setSuccessCount] = useState<number | null>(null);

  const { mutate, isPending, failureReason } = useMutation({
    ...trpc.directors.emailPlayers.mutationOptions(),
    onSuccess: (data) => {
      setSuccessCount(data.recipientCount);
    },
  });

  function handleSend() {
    if (!editorState || !subject.trim()) return;

    mutate({
      subject: subject.trim(),
      body: editorState as LexicalState,
      playerProfileIds,
    });
  }

  const handleEditorChange = useCallback(
    (state: EditorState, _editor: LexicalEditor) => {
      setEditorState(state.toJSON() as SerializedEditorState);
    },
    [],
  );

  function handleClose() {
    setSubject("");
    setEditorState(null);
    setSuccessCount(null);
    onOpenChange(false);
  }

  return (
    <Modal isOpen={isOpen} onOpenChange={handleClose} size="2xl">
      <div className="relative flex flex-col space-y-4 p-4">
        <Heading className={title({ size: "sm" })} slot="title">
          Email {playerProfileIds.length}{" "}
          {playerProfileIds.length === 1 ? "Player" : "Players"}
        </Heading>

        {successCount !== null ? (
          <div className="flex flex-col space-y-4">
            <Alert
              color="success"
              title="Email sent"
              description={`Email sent to ${successCount} ${successCount === 1 ? "player" : "players"}.`}
            />
            <div className="flex justify-end">
              <Button onPress={handleClose}>Close</Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col space-y-4">
            <TextField
              label="Subject"
              value={subject}
              onChange={setSubject}
              isRequired
            />

            <div>
              <span className="mb-1 block text-sm font-medium text-gray-700">
                Message
              </span>
              <RichTextEditor
                name="email-players"
                onChange={handleEditorChange}
                height="sm"
              />
            </div>

            {failureReason && (
              <Alert
                color="error"
                title="Failed to send"
                description={failureReason.message}
              />
            )}

            <div className="flex justify-end gap-2">
              <Button onPress={handleClose}>Cancel</Button>
              <Button
                variant="solid"
                color="primary"
                isDisabled={!subject.trim() || !editorState || isPending}
                onPress={handleSend}
              >
                {isPending ? "Sending..." : "Send"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
